import { useEffect, useMemo, useState } from "react";
import { Duration, intervalToDuration } from "date-fns";

import * as S from "./styles";
import Select from "components/Select";
import Button from "components/Button";
import { useManualOccurrence } from "contexts/register/ManualOccurrence";
import OccurrenceManagementService from "services/OccurrenceManagementService";
import { fireToast } from "services/sweetAlertConfig";
import Input from "components/Input";
import { socket } from "hooks/useSocket";
import NoSelectedReportError from "../NoSelectedReportError";
import { formatDate } from "utils/formatters";

type SelectOption = {
  label: string;
  value: number;
};

const selectData: SelectOption[] = [
  { label: "15min", value: 15 },
  { label: "30min", value: 30 },
  { label: "45min", value: 45 },
  { label: "60min", value: 60 },
  { label: "75min", value: 75 },
];

export default function ArrivalTimeTab() {
  const [duration, setDuration] = useState<Duration>({} as Duration);
  const [selectedInterval, setSelectedInterval] = useState<string>("15");
  const [timeToLeave, setTimeToLeave] = useState<Date>();
  const {
    refetch,
    selectedReport
  } = useManualOccurrence();

  const estimatedArrival = selectedReport?.estimatedArrival;
  const arrivalTime = selectedReport?.arrivalTime;
  const leaveTime = selectedReport?.leaveTime;

  async function handleArrivalUpdate() {
    const report = selectedReport;

    if (report) {
      try {
        await OccurrenceManagementService.updateArrival(report.id, new Date());
        fireToast(
          "Sinalização de chegada do pronta resposta efetuada com sucesso!"
        );
        refetch();
      } catch {
        fireToast("Ocorreu um erro ao sinalizar a chegada do agente.");
      }
    }
  }

  async function handleEstimatedArrivalUpdate() {
    const report = selectedReport;

    if (report) {
      try {
        await OccurrenceManagementService.updateEstimatedArrival(
          report.id,
          duration
        );
        fireToast(
          "Sinalização de estimativa de chegada do pronta resposta efetuada com sucesso!"
        );
        refetch();
      } catch (error) {
        fireToast(
          "Ocorreu um erro ao atualizar a estimativa de chegada do agente."
        );
      }
    }
  }
  async function handleTimeToLeaveUpdate() {
    const report = selectedReport;

    if (report && timeToLeave) {
      try {
        await OccurrenceManagementService.updateLeaveTime(
          report.id,
          timeToLeave
        );

        fireToast(
          "Sinalização de tempo de permanência do pronta resposta efetuada com sucesso!"
        );

        socket.emit("@dash/flag-patrol-time", {
          reportId: report.id,
          timeToLeave
        });

        refetch();
      } catch (error) {
        fireToast(
          "Ocorreu um erro ao atualizar o tempo de permanência do agente."
        );
      }
    }
  }
  function onTimeSelect(value: number) {
    const valueDuration = intervalToDuration({
      start: 0,
      end: value * 60 * 1000,
    });

    setDuration(valueDuration);
  }

  function handleDateTimeToDate(event: string) {
    const leaveDate = new Date(event);
    setTimeToLeave(leaveDate);
  }

  useEffect(() => {
    onTimeSelect(15);
  }, []);

  const formattedHours = useMemo(() => {
    const interval = new Date(
      new Date().getTime() + Number(selectedInterval) * 60 * 1000
    );

    const hours = String(interval.getHours()).padStart(2, "0");
    const minutes = String(interval.getMinutes()).padStart(2, "0");
    const seconds = String(interval.getSeconds()).padStart(2, "0");

    return `${hours}:${minutes}:${seconds}`;
  }, [selectedInterval]);

  const policemanId =
    selectedReport?.superficialPoliceman?.id ||
    selectedReport?.policeman?.user?.id;

  return (
    <div>
      {!selectedReport && (
        <NoSelectedReportError />
      )}

      {selectedReport && policemanId && (
        <>
          <S.Section>
            <Select
              label="Inserir estimativa de chegada do pronta-resposta"
              id="arrivalTime"
              required
              disabled={!!estimatedArrival}
              name="arrivalTime"
              onChange={(event) => {
                onTimeSelect(Number(event.target.value));
                setSelectedInterval(event.target.value);
              }}
            >
              {selectData.map(({ value, label }) => (
                <option key={label} value={String(value)}>
                  {label}
                </option>
              ))}
            </Select>
            <S.ButtonWrapper>
              {estimatedArrival ? (
                <S.Badge>
                  <span>
                    Chegada estimada para {estimatedArrival.toLocaleString()}
                  </span>
                </S.Badge>
              ) : (
                <Button
                  title={`Registrar estimativa de chegada para ${formattedHours}`}
                  onClick={handleEstimatedArrivalUpdate}
                />
              )}
            </S.ButtonWrapper>
          </S.Section>

          <S.Section>
            <div>
              <h3>Sinalizar chegada do pronta-resposta</h3>
              <p>
                Pressionar o botão abaixo irá sinalizar a hora atual como a
                chegada do agente ao local.
              </p>
            </div>

            <S.ButtonWrapper>
              {arrivalTime ? (
                <S.Badge>
                  <span>
                    A chegada do pronta-resposta foi registrada às{" "}
                    {formatDate(new Date(arrivalTime))}
                  </span>
                </S.Badge>
              ) : (
                <Button
                  onClick={handleArrivalUpdate}
                  title={`Sinalizar chegada do agente ao local`}
                />
              )}
            </S.ButtonWrapper>
          </S.Section>
          <S.Section>
            <Input
              label="Insira até que horas o PR deverá ficar no local"
              id="leaveTime"
              name="leaveTime"
              required
              type={"datetime-local"}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleDateTimeToDate(e.target.value);
              }}
            />
            <div>
              <S.ButtonWrapper>
                <Button
                    title={`${leaveTime?'Atualizar' :'Registrar'} horário de permanência no local`}
                    onClick={handleTimeToLeaveUpdate}
                  />
              </S.ButtonWrapper>
              <S.ButtonWrapper>
                <div>
                {leaveTime &&
                  <S.Badge>
                    <span>
                      O pronta-resposta deve permanecer até às{" "}
                      {formatDate(new Date(leaveTime))}
                    </span>
                  </S.Badge>
                  }
                </div>
                </S.ButtonWrapper>
            </div>
            </S.Section>
        </>
      )}
    </div>
  );
}
